// Display helpers for the landing editor. Dates show in Cairo time (the
// academy's time zone, the one the site prints), whatever the browser's zone.
const TZ = 'Africa/Cairo';

const safeDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

/** "24 Sep 2026, 14:02" (Cairo) or '' */
export function formatDateTime(iso: string | null | undefined): string {
  const d = safeDate(iso);
  if (!d) return '';
  return d.toLocaleString('en-GB', {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "14:02" (Cairo) or '' */
export function formatTime(iso: string | null | undefined): string {
  const d = safeDate(iso);
  if (!d) return '';
  return d.toLocaleTimeString('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
}

/** A stored calendar date (<date>T00:00:00.000Z) → "24 Sep 2026". Read as UTC so it never shifts a day. */
export function formatDay(iso: string | null | undefined): string {
  const d = safeDate(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** 12000 → "EGP 12,000" */
export function formatEgp(n: number | null | undefined): string {
  if (typeof n !== 'number' || !isFinite(n)) return '—';
  return `EGP ${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

/** 2411724 → "2.3 MB"; 81920 → "80 KB" */
export function formatBytes(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes <= 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** A 'YYYY-MM' end month → "Sep 2025" / "سبتمبر 2025" (the site's wording), or ''. */
export function formatMonth(ym: string | null | undefined, lang: 'en' | 'ar' = 'en'): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym || '');
  if (!m) return ym || '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 15, 12));
  return d.toLocaleDateString(lang === 'ar' ? 'ar-EG-u-nu-latn-ca-gregory' : 'en-GB', {
    timeZone: 'UTC',
    month: lang === 'ar' ? 'long' : 'short',
    year: 'numeric',
  });
}

const AR_ORDINAL = ['', 'الأولى', 'الثانية', 'الثالثة', 'الرابعة', 'الخامسة', 'السادسة', 'السابعة', 'الثامنة', 'التاسعة', 'العاشرة'];

/** 1 → "الدفعة الأولى", 14 → "الدفعة 14" (the site's batch label). */
export const batchLabelAr = (n: number) => (n >= 1 && n <= 10 ? `الدفعة ${AR_ORDINAL[n]}` : `الدفعة ${n}`);

/** "طالب واحد", "طالبان", "5 طلاب", "24 طالبًا", "100 طالب" (the site's plural). */
export function studentsAr(n: number): string {
  let rule = 'other';
  try {
    rule = new Intl.PluralRules('ar').select(n);
  } catch {
    // no Intl plural data: fall back to the plain form
  }
  if (rule === 'one') return 'طالب واحد';
  if (rule === 'two') return 'طالبان';
  if (rule === 'few') return `${n} طلاب`;
  if (rule === 'many') return `${n} طالبًا`;
  return `${n} طالب`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** BatchConfig → "Sun & Wed 19:00 · 180 min" */
export function formatSchedule(
  s: { weekdays: number[]; startTime: string; durationMinutes: number } | null | undefined
): string {
  if (!s || !s.weekdays || !s.weekdays.length) return '';
  const days = s.weekdays.map((d) => WEEKDAYS[d] || String(d));
  const dayText = days.length > 1 ? `${days.slice(0, -1).join(', ')} & ${days[days.length - 1]}` : days[0];
  return `${dayText} ${s.startTime} · ${s.durationMinutes} min`;
}
